import type { ArenaId } from "./arenas.js";

// Prices are fixed point bigints with 8 decimals, so comparing a start and end price is exact.
export const PRICE_DECIMALS = 8;
const SCALE = 10n ** BigInt(PRICE_DECIMALS);

export type Price = bigint;

export function toPrice(value: string | number): Price {
  const s = typeof value === "number" ? value.toFixed(PRICE_DECIMALS) : value.trim();
  const m = /^(\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) throw new Error(`Not a valid price: ${value}`);
  const frac = (m[2] ?? "").slice(0, PRICE_DECIMALS).padEnd(PRICE_DECIMALS, "0");
  return BigInt(m[1]!) * SCALE + BigInt(frac);
}

export function formatPrice(p: Price, decimals = 2): string {
  const whole = p / SCALE;
  const frac = (p % SCALE).toString().padStart(PRICE_DECIMALS, "0").slice(0, decimals);
  return decimals > 0 ? `${whole}.${frac}` : `${whole}`;
}

export interface Quote {
  source: string;
  price: Price;
  /** When the source says the price was valid, in ms. */
  at: number;
}

export interface OracleRules {
  /** A quote older than this is ignored. */
  maxAgeMs: number;
  /** If the fresh quotes spread wider than this around the median, nobody can say what the price was. */
  maxSpreadBps: number;
  /** Fewer fresh sources than this and the snapshot fails. */
  minSources: number;
}

/** Gold moves less than crypto, so a smaller disagreement already means something is wrong. */
export const ORACLE_RULES: Record<ArenaId, OracleRules> = {
  BTC: { maxAgeMs: 3_000, maxSpreadBps: 30, minSources: 2 },
  ETH: { maxAgeMs: 3_000, maxSpreadBps: 30, minSources: 2 },
  SOL: { maxAgeMs: 3_000, maxSpreadBps: 40, minSources: 2 },
  TON: { maxAgeMs: 3_000, maxSpreadBps: 50, minSources: 2 },
  XAU: { maxAgeMs: 5_000, maxSpreadBps: 15, minSources: 2 },
};

export type Snapshot =
  | { ok: true; price: Price; sources: string[]; at: number }
  | { ok: false; reason: "too_few_sources" | "sources_disagree"; detail: string };

/**
 * One price snapshot for an arena: the average of the fresh quotes that agree with each other
 * (3 or 4 sources). Agreement is judged around the median, so one wild exchange can't drag the price.
 * The start price is locked at match time and the end price at 3:00. If either snapshot
 * fails, the battle is refunded in full (screen 52), never decided on a doubtful price.
 */
export function snapshot(quotes: Quote[], now: number, rules: OracleRules): Snapshot {
  const fresh = quotes.filter((q) => q.price > 0n && now - q.at <= rules.maxAgeMs && q.at <= now + 1_000);
  if (fresh.length < rules.minSources) {
    return { ok: false, reason: "too_few_sources", detail: `${fresh.length} fresh of ${quotes.length}` };
  }
  // One stale or jumpy exchange must not spoil a battle: keep the sources that sit close to the median
  // (within half the allowed spread) and price from those. Fewer than two in agreement means a refund.
  const median = medianOf(fresh.map((q) => q.price));
  const halfBand = BigInt(rules.maxSpreadBps) * median;
  const agreeing = fresh.filter((q) => abs(q.price - median) * 20_000n <= halfBand);
  if (agreeing.length < rules.minSources) {
    const worst = fresh.reduce((w, q) => (abs(q.price - median) > abs(w.price - median) ? q : w));
    const bps = (abs(worst.price - median) * 10_000n) / median;
    return { ok: false, reason: "sources_disagree", detail: `${agreeing.length} agree, ${worst.source} ${bps} bps out` };
  }
  // The average moves whenever any agreeing exchange moves, so the price stays alive in a quiet market.
  const sum = agreeing.reduce((t, q) => t + q.price, 0n);
  return { ok: true, price: sum / BigInt(agreeing.length), sources: agreeing.map((q) => q.source), at: now };
}

function medianOf(prices: Price[]): Price {
  const sorted = [...prices].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2n;
}

const abs = (x: bigint): bigint => (x < 0n ? -x : x);

export type Move = "up" | "down" | "flat";

/** Long wins on up, short wins on down, and an exactly equal price is a draw with full refunds. */
export function priceMove(start: Price, end: Price): Move {
  return end > start ? "up" : end < start ? "down" : "flat";
}
